import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { BriefcaseIcon, SearchIcon, FilterIcon, ChevronDownIcon, CheckCircleIcon, XCircleIcon, EyeIcon, CalendarIcon, BuildingIcon, GraduationCapIcon, AlertCircleIcon, FileTextIcon, ClockIcon } from 'lucide-react';
const PartnershipApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [employerFilter, setEmployerFilter] = useState('All Employers');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Sample partnership approval data
  const partnershipApprovals = [{
    id: 'ps001',
    title: 'ADNOC Future Engineers',
    employer: 'ADNOC',
    program: 'Petroleum Engineering',
    preferredUniversity: 'Imperial College London',
    alternativeUniversities: ['Stanford University', 'University of Texas at Austin'],
    submissionDate: '2023-06-10',
    status: 'pending',
    budget: '$2,500,000',
    duration: '4 years',
    scholarshipCount: 5,
    description: 'Comprehensive scholarship program for petroleum engineering students with guaranteed employment upon graduation.',
    requirements: ['UAE National', 'GPA 3.5+', 'IELTS 6.5+', 'Technical aptitude test'],
    employmentGuarantee: true,
    internshipProgram: true,
    contactPerson: 'Dr. Ahmed Al Mansouri',
    contactEmail: 'ahmed.mansouri@adnoc.ae',
    lastReviewDate: '2023-06-15',
    reviewNotes: 'Strong program with clear career path. Need clarification on university selection criteria.'
  }, {
    id: 'ps002',
    title: 'Etisalat Digital Innovators',
    employer: 'Etisalat',
    program: 'Computer Science',
    preferredUniversity: 'Multiple Options',
    alternativeUniversities: ['MIT', 'Stanford University', 'University of Cambridge'],
    submissionDate: '2023-06-08',
    status: 'pending',
    budget: '$1,800,000',
    duration: '4 years',
    scholarshipCount: 3,
    description: 'Scholarship program focused on digital innovation and telecommunications technology.',
    requirements: ['UAE National', 'GPA 3.7+', 'IELTS 7.0+', 'Programming skills assessment'],
    employmentGuarantee: true,
    internshipProgram: true,
    contactPerson: 'Ms. Fatima Al Zaabi',
    contactEmail: 'fatima.zaabi@etisalat.ae',
    lastReviewDate: '2023-06-12',
    reviewNotes: 'Excellent program alignment with UAE Vision 2071. Awaiting final budget approval.'
  }, {
    id: 'ps003',
    title: 'Emirates Aviation Leaders',
    employer: 'Emirates',
    program: 'Aviation Management',
    preferredUniversity: 'Embry-Riddle Aeronautical University',
    alternativeUniversities: ['Cranfield University', 'Delft University of Technology'],
    submissionDate: '2023-06-05',
    status: 'approved',
    budget: '$1,200,000',
    duration: '3 years',
    scholarshipCount: 4,
    description: 'Leadership development program for future aviation industry professionals.',
    requirements: ['UAE National', 'GPA 3.3+', 'IELTS 6.5+', 'Leadership experience'],
    employmentGuarantee: true,
    internshipProgram: true,
    contactPerson: 'Mr. Mohammed Al Hashimi',
    contactEmail: 'mohammed.hashimi@emirates.com',
    lastReviewDate: '2023-06-18',
    reviewNotes: 'Approved with minor modifications to internship structure.'
  }, {
    id: 'ps004',
    title: 'Mubadala Tech Innovators',
    employer: 'Mubadala',
    program: 'Data Science',
    preferredUniversity: 'Multiple Options',
    alternativeUniversities: ['Carnegie Mellon', 'University of California Berkeley', 'Oxford University'],
    submissionDate: '2023-06-12',
    status: 'under_review',
    budget: '$2,000,000',
    duration: '4 years',
    scholarshipCount: 4,
    description: 'Advanced data science and AI scholarship program with focus on innovation.',
    requirements: ['UAE National', 'GPA 3.6+', 'IELTS 7.0+', 'Mathematics proficiency'],
    employmentGuarantee: true,
    internshipProgram: true,
    contactPerson: 'Dr. Sara Al Dhaheri',
    contactEmail: 'sara.dhaheri@mubadala.ae',
    lastReviewDate: '2023-06-16',
    reviewNotes: 'Currently under technical review by the academic committee.'
  }, {
    id: 'ps005',
    title: 'DP World Logistics Excellence',
    employer: 'DP World',
    program: 'Supply Chain Management',
    preferredUniversity: 'London Business School',
    alternativeUniversities: ['INSEAD', 'Wharton School'],
    submissionDate: '2023-06-14',
    status: 'rejected',
    budget: '$900,000',
    duration: '2 years',
    scholarshipCount: 2,
    description: 'MBA program focused on global logistics and supply chain management.',
    requirements: ['UAE National', 'GPA 3.4+', 'GMAT 650+', 'Work experience 3+ years'],
    employmentGuarantee: true,
    internshipProgram: false,
    contactPerson: 'Mr. Khalid Al Mazrouei',
    contactEmail: 'khalid.mazrouei@dpworld.com',
    lastReviewDate: '2023-06-20',
    reviewNotes: 'Rejected due to insufficient budget allocation for the proposed scope.'
  }];
  const statusOptions = ['All', 'pending', 'under_review', 'approved', 'rejected'];
  const employerOptions = ['All Employers', 'ADNOC', 'Etisalat', 'Emirates', 'Mubadala', 'DP World'];
  const getStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = status => {
    switch (status) {
      case 'under_review':
        return 'Under Review';
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseInt(amount.replace(/[$,]/g, '')));
  };
  // Filter approvals
  const filteredApprovals = partnershipApprovals.filter(approval => {
    const matchesSearch = searchQuery === '' || approval.title.toLowerCase().includes(searchQuery.toLowerCase()) || approval.employer.toLowerCase().includes(searchQuery.toLowerCase()) || approval.program.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || approval.status === statusFilter;
    const matchesEmployer = employerFilter === 'All Employers' || approval.employer === employerFilter;
    return matchesSearch && matchesStatus && matchesEmployer;
  });
  const openDetailModal = approval => {
    setSelectedApproval(approval);
    setIsDetailModalOpen(true);
  };
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedApproval(null);
  };
  return <Layout title="Partnership Approvals" persona="MoHESR Admin" data-id="element-3077">
      <div className="max-w-7xl mx-auto" data-id="element-3078">
        {/* Header with stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-3079">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3080">
            <div className="flex items-start justify-between" data-id="element-3081">
              <div data-id="element-3082">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-3083">
                  Total Submissions
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-3084">23</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3085">This quarter</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-3086">
                <BriefcaseIcon className="w-6 h-6 text-primary-500" data-id="element-3087" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3088">
            <div className="flex items-start justify-between" data-id="element-3089">
              <div data-id="element-3090">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-3091">
                  Pending Review
                </h3>
                <div className="text-3xl font-bold text-yellow-500" data-id="element-3092">3</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3093">Require action</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full" data-id="element-3094">
                <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-3095" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3096">
            <div className="flex items-start justify-between" data-id="element-3097">
              <div data-id="element-3098">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-3099">Approved</h3>
                <div className="text-3xl font-bold text-green-500" data-id="element-3100">15</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3101">65% approval rate</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full" data-id="element-3102">
                <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-3103" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-3104">
            <div className="flex items-start justify-between" data-id="element-3105">
              <div data-id="element-3106">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-3107">Total Budget</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-3108">
                  $18.2M
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3109">
                  Approved partnerships
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full" data-id="element-3110">
                <GraduationCapIcon className="w-6 h-6 text-blue-500" data-id="element-3111" />
              </div>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-3112">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-3113">
            <div className="relative flex-grow max-w-md" data-id="element-3114">
              <input type="text" placeholder="Search partnerships..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-3115" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-3116" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-3117">
            <div className="relative" data-id="element-3118">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3119">
                {statusOptions.map(status => <option key={status} value={status} data-id="element-3120">
                    Status: {status === 'All' ? 'All' : getStatusText(status)}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-3121" />
            </div>
            <div className="relative" data-id="element-3122">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={employerFilter} onChange={e => setEmployerFilter(e.target.value)} data-id="element-3123">
                {employerOptions.map(employer => <option key={employer} value={employer} data-id="element-3124">
                    {employer}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-3125" />
            </div>
          </div>
        </div>
        {/* Approvals Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-3126">
          <div className="overflow-x-auto" data-id="element-3127">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3128">
              <thead className="bg-gray-50" data-id="element-3129">
                <tr data-id="element-3130">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3131">
                    Partnership
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3132">
                    Employer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3133">
                    Program
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3134">
                    Budget
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3135">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3136">
                    Submitted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3137">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3138">
                {filteredApprovals.map(approval => <tr key={approval.id} className="hover:bg-gray-50" data-id="element-3139">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3140">
                      <div data-id="element-3141">
                        <div className="text-sm font-medium text-gray-900" data-id="element-3142">
                          {approval.title}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3143">
                          {approval.scholarshipCount} scholarships
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3144">
                      <div className="text-sm text-gray-900" data-id="element-3145">
                        {approval.employer}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3146">
                      <div className="text-sm text-gray-900" data-id="element-3147">
                        {approval.program}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-3148">
                        {approval.preferredUniversity}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3149">
                      <div className="text-sm text-gray-900" data-id="element-3150">
                        {formatCurrency(approval.budget)}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-3151">
                        {approval.duration}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3152">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(approval.status)}`} data-id="element-3153">
                        {getStatusText(approval.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3154">
                      {formatDate(approval.submissionDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3155">
                      <div className="flex space-x-2" data-id="element-3156">
                        <button className="text-primary-500 hover:text-primary-700" title="View Details" onClick={() => openDetailModal(approval)} data-id="element-3157">
                          <EyeIcon className="w-4 h-4" data-id="element-3158" />
                        </button>
                        {(approval.status === 'pending' || approval.status === 'under_review') && <>
                            <button className="text-green-500 hover:text-green-700" title="Approve" data-id="element-3159">
                              <CheckCircleIcon className="w-4 h-4" data-id="element-3160" />
                            </button>
                            <button className="text-red-500 hover:text-red-700" title="Reject" data-id="element-3161">
                              <XCircleIcon className="w-4 h-4" data-id="element-3162" />
                            </button>
                          </>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        {/* Detail Modal */}
        {isDetailModalOpen && selectedApproval && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3163">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3164">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal} data-id="element-3165"></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-3166">
                &#8203;
              </span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-3167">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3168">
                  <div className="flex justify-between items-start mb-6" data-id="element-3169">
                    <div data-id="element-3170">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3171">
                        {selectedApproval.title}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-3172">
                        {selectedApproval.employer}
                      </p>
                    </div>
                    <button onClick={closeDetailModal} className="text-gray-400 hover:text-gray-600" data-id="element-3173">
                      <XCircleIcon className="w-6 h-6" data-id="element-3174" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3175">
                    {/* Basic Information */}
                    <div data-id="element-3176">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3177">
                        Basic Information
                      </h4>
                      <div className="space-y-3" data-id="element-3178">
                        <div data-id="element-3179">
                          <span className="text-sm text-gray-500" data-id="element-3180">
                            Program:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3181">
                            {selectedApproval.program}
                          </p>
                        </div>
                        <div data-id="element-3182">
                          <span className="text-sm text-gray-500" data-id="element-3183">
                            Preferred University:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3184">
                            {selectedApproval.preferredUniversity}
                          </p>
                        </div>
                        <div data-id="element-3185">
                          <span className="text-sm text-gray-500" data-id="element-3186">
                            Alternative Universities:
                          </span>
                          <ul className="text-sm text-gray-900 list-disc list-inside" data-id="element-3187">
                            {selectedApproval.alternativeUniversities.map((uni, index) => <li key={index} data-id="element-3188">{uni}</li>)}
                          </ul>
                        </div>
                        <div data-id="element-3189">
                          <span className="text-sm text-gray-500" data-id="element-3190">Budget:</span>
                          <p className="text-sm text-gray-900" data-id="element-3191">
                            {formatCurrency(selectedApproval.budget)}
                          </p>
                        </div>
                        <div data-id="element-3192">
                          <span className="text-sm text-gray-500" data-id="element-3193">
                            Duration:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3194">
                            {selectedApproval.duration}
                          </p>
                        </div>
                        <div data-id="element-3195">
                          <span className="text-sm text-gray-500" data-id="element-3196">
                            Number of Scholarships:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3197">
                            {selectedApproval.scholarshipCount}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Program Details */}
                    <div data-id="element-3198">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3199">
                        Program Details
                      </h4>
                      <div className="space-y-3" data-id="element-3200">
                        <div data-id="element-3201">
                          <span className="text-sm text-gray-500" data-id="element-3202">
                            Description:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3203">
                            {selectedApproval.description}
                          </p>
                        </div>
                        <div data-id="element-3204">
                          <span className="text-sm text-gray-500" data-id="element-3205">
                            Requirements:
                          </span>
                          <ul className="text-sm text-gray-900 list-disc list-inside" data-id="element-3206">
                            {selectedApproval.requirements.map((req, index) => <li key={index} data-id="element-3207">{req}</li>)}
                          </ul>
                        </div>
                        <div className="flex items-center space-x-4" data-id="element-3208">
                          <div className="flex items-center" data-id="element-3209">
                            <span className="text-sm text-gray-500 mr-2" data-id="element-3210">
                              Employment Guarantee:
                            </span>
                            {selectedApproval.employmentGuarantee ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3211" /> : <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-3212" />}
                          </div>
                          <div className="flex items-center" data-id="element-3213">
                            <span className="text-sm text-gray-500 mr-2" data-id="element-3214">
                              Internship Program:
                            </span>
                            {selectedApproval.internshipProgram ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3215" /> : <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-3216" />}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Contact Information */}
                    <div data-id="element-3217">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3218">
                        Contact Information
                      </h4>
                      <div className="space-y-3" data-id="element-3219">
                        <div data-id="element-3220">
                          <span className="text-sm text-gray-500" data-id="element-3221">
                            Contact Person:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3222">
                            {selectedApproval.contactPerson}
                          </p>
                        </div>
                        <div data-id="element-3223">
                          <span className="text-sm text-gray-500" data-id="element-3224">Email:</span>
                          <p className="text-sm text-gray-900" data-id="element-3225">
                            {selectedApproval.contactEmail}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Review Information */}
                    <div data-id="element-3226">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-3227">
                        Review Information
                      </h4>
                      <div className="space-y-3" data-id="element-3228">
                        <div data-id="element-3229">
                          <span className="text-sm text-gray-500" data-id="element-3230">Status:</span>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedApproval.status)}`} data-id="element-3231">
                            {getStatusText(selectedApproval.status)}
                          </span>
                        </div>
                        <div data-id="element-3232">
                          <span className="text-sm text-gray-500" data-id="element-3233">
                            Last Review Date:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3234">
                            {formatDate(selectedApproval.lastReviewDate)}
                          </p>
                        </div>
                        <div data-id="element-3235">
                          <span className="text-sm text-gray-500" data-id="element-3236">
                            Review Notes:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-3237">
                            {selectedApproval.reviewNotes}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Modal Actions */}
                {(selectedApproval.status === 'pending' || selectedApproval.status === 'under_review') && <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3238">
                    <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3239">
                      Approve
                    </button>
                    <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3240">
                      Reject
                    </button>
                    <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm" onClick={closeDetailModal} data-id="element-3241">
                      Close
                    </button>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </Layout>;
};
export default PartnershipApprovals;